/**
 * Two ways of writing a language, made comparable.
 *
 * A caregiver's languages arrive from the caregiver form, which suggests ISO
 * 639-1 codes (`en`, `es`). A client's language preference arrives from
 * intake as whatever the coordinator typed — `Spanish`, `Español`. Rule 7
 * compared the two strings as they were, so a client who needs a Spanish
 * speaker could never be booked with anybody, the Spanish speakers included:
 * `es` is not `spanish`. The synthetic seed is what showed it.
 *
 * `languageKey` maps the codes, the English names and the languages' own
 * names to one key. Anything it does not know is compared as typed — trimmed
 * and lower-cased — so an unlisted language still matches itself, and the
 * table only ever widens what matches. It never narrows it.
 *
 * The list is the languages this workforce and these clients actually speak,
 * not all of ISO 639. Adding one is a line.
 */
const ALIASES = {
    en: ['english'],
    es: ['spanish', 'español', 'espanol', 'castellano'],
    fr: ['french', 'français', 'francais'],
    ht: ['haitian creole', 'haitian', 'kreyòl', 'kreyol', 'creole'],
    pt: ['portuguese', 'português', 'portugues'],
    zh: ['chinese', 'mandarin', '中文', '普通话'],
    yue: ['cantonese', '廣東話'],
    vi: ['vietnamese', 'tiếng việt', 'tieng viet'],
    ko: ['korean', '한국어'],
    tl: ['tagalog', 'filipino'],
    ru: ['russian', 'русский'],
    ar: ['arabic', 'العربية'],
    am: ['amharic', 'አማርኛ'],
    yo: ['yoruba', 'yorùbá'],
    ig: ['igbo'],
    tw: ['twi', 'akan'],
    kri: ['krio'],
    hi: ['hindi', 'हिन्दी'],
    pl: ['polish', 'polski'],
    it: ['italian', 'italiano'],
};
const BY_NAME = new Map(Object.entries(ALIASES).flatMap(([code, names]) => [
    [code, code],
    ...names.map((name) => [name, code]),
]));
/** The comparable form of a language, written as a code or a name. */
export function languageKey(value) {
    const typed = value.trim().toLowerCase();
    return BY_NAME.get(typed) ?? typed;
}
/** True when the two say the same language, however each was written. */
export function sameLanguage(a, b) {
    return languageKey(a) === languageKey(b);
}
//# sourceMappingURL=language.js.map